/**
 * @file facetHelpers.ts
 *
 * @fileoverview A set of utilities to help with facet setting and unsetting. When
 * we deal with (especially) multivalue setting we often need to calculate all children of an item,
 * or all parents of an item. These functions are helpers to do just that.
 */

import { Facet, FacetValue } from '~/jobiqo/models/search';

/**
 * Returns all children of a particular facet item.
 *
 * @param {string} filter
 *   The filter name for the item we are checking.
 * @param {FacetValue[]} values
 *   All the facet values for this facet including all children.
 *
 * @return {FacetValue}
 *   The facet values that are children of this particular filter.
 */
export const getAllChildren = (
  filter: string,
  values: FacetValue[],
): FacetValue[] => {
  let all: any[] = [];
  values.forEach((facetValue) => {
    // If we are on the right item start checking children. otherwise keep looking into children and let
    // also the forEach continue.
    if (facetValue.filter === filter && facetValue.children) {
      // add each child item to the list.
      all = [...all, ...facetValue.children.map((i) => i)];
      // Do the same process for the child's children (recursively go through the whole tree).
      const allChildrenOfChild = facetValue.children.map((i) => {
        return getAllChildren(
          i.filter,
          facetValue.children
            ? facetValue.children.filter((item) => item.filter === i.filter)
            : [], // Look only in this item's tree.
        );
      });
      if (allChildrenOfChild.length > 0) {
        all = [...all, ...allChildrenOfChild[0]];
      }
    } else {
      // Keep iterating into children to look for it, maybe its deeper in this element's tree.
      all = [
        ...all,
        ...getAllChildren(
          filter,
          facetValue.children ? facetValue.children : [],
        ),
      ];
    }
  });
  return all;
};

/**
 * Returns all parents of a particular facet item.
 *
 * @param {string} filter
 *   The filter name for the item we are checking.
 * @param {FacetValue[]} values
 *   All the facet values for this facet including all children.
 *
 * @return {FacetValue[]}
 *   The facet values that are parents of this particular filter.
 */
export const getAllParents = (
  filter: string,
  values: FacetValue[],
  allValues?: FacetValue[],
): string[] => {
  let all: string[] = [];
  values.forEach((facetValue) => {
    // If any of the children is the filter we add it.
    let hasFilterInChildren = false;
    if (facetValue.children) {
      hasFilterInChildren = facetValue.children
        .map((child) => child.filter)
        .includes(filter);
    }

    if (facetValue.children && hasFilterInChildren) {
      all = [...all, facetValue.filter];
      // Once we find a parent we need to start looking again for parents of it.
      if (allValues) {
        all = [...all, ...getAllParents(facetValue.filter, allValues)];
      }
    } else {
      all = [
        ...all,
        ...getAllParents(
          filter,
          facetValue.children ? facetValue.children : [],
          values,
        ),
      ];
    }
  });
  return all;
};

/**
 * Returns the ancestry chain for a facet item from root to selected filter.
 *
 * @param {string} filter
 *   The filter value to find.
 * @param {FacetValue[]} values
 *   All the facet values for this facet including all children.
 *
 * @return {FacetValue[]}
 *   The chain from root to the selected facet value.
 */
export const getFacetAncestryChain = (
  filter: string,
  values: FacetValue[],
): FacetValue[] => {
  const findChain = (
    searchValues: FacetValue[],
    ancestors: FacetValue[] = [],
  ): FacetValue[] => {
    for (const facetValue of searchValues) {
      if (facetValue.filter === filter) {
        return [...ancestors, facetValue];
      }

      if (facetValue.children?.length) {
        const chain = findChain(facetValue.children, [
          ...ancestors,
          facetValue,
        ]);

        if (chain.length) {
          return chain;
        }
      }
    }

    return [];
  };

  return findChain(values);
};

/**
 * Clears from selectedItems with children, when a parent is unselected children must also be removed
 * from the list of selectedItems.
 *
 * @param {string} filter
 *   The filter name for the item we are clearing.
 * @param {Facet} facet
 *   The facet object we are clearing.
 * @param {string[]} selectedItems
 *   The current selected items.
 *
 * @return {string[]}
 *   The resulting seledted items after clearing with children for the item given.
 */
export const clearWithChildren = (
  filter: string,
  facet: Facet,
  selectedItems: string[],
): string[] => {
  let childrenKeys: string[] = [];
  childrenKeys = getAllChildren(filter, facet.values).map((child) => {
    return child.filter;
  });

  return selectedItems.filter((item) => {
    return item !== filter && !childrenKeys.includes(item);
  });
};

/**
 * Method to help see if a filter is selected.
 *
 * Will check both the actual filter but also any child, if a child is selected it should be considered
 * as selected.
 *
 * @param {string} filter
 *   The filter we want to check is selected or not
 * @param {FacetValue[]} values
 *   All the facet values for this facet including all children.
 * @param {string[]} selectedItems
 *   The current selected items.
 *
 * @returns boolean
 *   Return true if selected, false otherwise.
 */
export const checkFacetExists = (
  filter: string,
  facetValues: FacetValue[],
  selectedItems: string[],
): boolean => {
  let itemAndChildrenKeys: string[] = [];
  const childrenKeys = getAllChildren(filter, facetValues).map(
    (child) => child.filter,
  );
  itemAndChildrenKeys = [...childrenKeys, filter];
  return selectedItems.some((item) => {
    return itemAndChildrenKeys.includes(item);
  });
};

/**
 * Return the facet label including counting.
 *
 * @param {FacetValue} facetItem
 *   The facet item we want to get the label for
 *
 * @returns string
 *   The facet label.
 */
export const getFacetLabel = (facetItem: FacetValue): string => {
  return `${facetItem.label ? facetItem.label : facetItem.filter} (${
    facetItem.totalCount
  })`;
};

/**
 * Generates a machine name for the label of a facet.
 * @param label
 *   The facet label.
 * @returns
 *   The machine name.
 */
export const getFacetMachineName = (label: string): string => {
  return label ? label.replace(/\s/g, '-').toLowerCase() : label;
};

/**
 * Returns concatenated facets' labels.
 *
 * @param {Facet[]} facets
 *  The facets.
 * @param {string} concatenator
 *  The concatenator string.
 * @param {boolean} trimBrackets
 * @default true
 *  The flag to trim brackets and text in between
 * @returns
 *  Concatenated facet labels.
 */
export const concatFacetsLabels = (
  facets: Facet[],
  concatenator = ' | ',
  trimBrackets = true,
): string => {
  const concatenation = facets
    .map((facet: Facet) =>
      [...facet.values].map((facetValue: FacetValue) =>
        getFacetLabel(facetValue),
      ),
    )
    .join(concatenator)
    .replace(/\[+([^\][]+)]+/g, '<div>$1</div>');

  return trimBrackets
    ? // Remove brackets and text in between.
      concatenation.replace(/ *\([^)]*\)*/g, '')
    : concatenation;
};

/**
 * Returns concatenated facet's labels.
 *
 * @param {Facet} facet
 *  The facet.
 * @param {string} concatenator
 *  The concatenator string.
 * @returns
 *  Concatenated facet labels.
 */
export const concatFacetLabels = (facet: Facet, concatenator = ' | '): string =>
  facet.values
    .map((facetValue: FacetValue) => getFacetLabel(facetValue))
    .join(concatenator);
